/**
 * The single COMBAT.app keyboard root: one window listener owns esc dismissal, undo/redo, and the
 * EDIT-mode authoring tool scheme, subscribed ONCE and reading the latest closures through refs (so
 * it never goes stale and never re-subscribes on every render). COMBAT.app holds the shell's stage,
 * and the rail pages beside it are forms whose own inputs swallow keys, so a window listener is
 * scoped well enough.
 *
 * The refs it hands back are filled in by the component AFTER its loading early-return —
 * `undoRef`/`redoRef` once the logged undo/redo exist, `editKbRef` once `viewMode`/`toolsDisabled`
 * are known, `railKeyRef` once the tool rail's context is, `holdMeasureRef` once the Measure tool
 * is. All default safe (no-ops / PLAY+disabled) so a keystroke during loading does nothing.
 *
 * Hold-M (`projects/measurement-tool.md` ruling 8) is the one held key: keydown starts the measure
 * gesture, and keyup or a window blur ends it, so a key released outside the tab never strands it.
 */
import { useCallback, useEffect, useRef, useState, type Dispatch, type RefObject, type SetStateAction } from "react";
import type { EscLayers } from "../../../lib/encounter/escLayers";
import type { CombatBrush } from "../combatAuthoring";
import type { CombatTool } from "../CombatStage";
import { isTextEntry } from "./predicates";

export function useCombatKeyboard({
  esc,
  selectedId,
  setSelectedId,
  setBrush,
  setTool,
  pickBrush,
}: {
  esc: EscLayers;
  selectedId: string | null;
  setSelectedId: Dispatch<SetStateAction<string | null>>;
  setBrush: Dispatch<SetStateAction<CombatBrush | null>>;
  setTool: Dispatch<SetStateAction<CombatTool>>;
  pickBrush: (b: CombatBrush | null) => void;
}): {
  undoRef: RefObject<() => void>;
  redoRef: RefObject<() => void>;
  editKbRef: RefObject<{ viewMode: "edit" | "play"; toolsDisabled: boolean }>;
  railKeyRef: RefObject<(key: string) => boolean>;
  holdMeasureRef: RefObject<{ start: () => void; end: () => void }>;
  undoRedoSeq: number;
  bumpUndoRedoSeq: () => void;
} {
  const escRef = useRef(esc);
  escRef.current = esc;
  // The keyboard shortcuts go through the same logged undo/redo the menubar buttons do (defined once
  // the encounter has loaded, below), so a `Ctrl/Cmd+Z` records into the LOG feed too. Seeded as
  // no-ops; reassigned to `doUndo`/`doRedo` after the early-return guard (nothing to undo while loading).
  const undoRef = useRef<() => void>(() => {});
  const redoRef = useRef<() => void>(() => {});
  // A monotonic counter bumped on every local undo/redo, threaded to `CombatStage` as `undoRedoSeq` —
  // the strand guard that voids an in-flight PLAY-move ghost a rewind rewrites (see that prop's doc).
  const [undoRedoSeq, setUndoRedoSeq] = useState(0);
  const bumpUndoRedoSeq = useCallback(() => setUndoRedoSeq((n) => n + 1), []);
  const selectedIdRef = useRef(selectedId);
  selectedIdRef.current = selectedId;
  // The EDIT-authoring keyboard scheme's gating, read by the once-
  // subscribed listener through a ref so it stays current. Defaults safe (PLAY / disabled) until loaded.
  const editKbRef = useRef<{ viewMode: "edit" | "play"; toolsDisabled: boolean }>({ viewMode: "play", toolsDisabled: true });
  // The board tool rail's keys (`BoardToolRail.tsx`): returns whether the key pressed a rail tool.
  const railKeyRef = useRef<(key: string) => boolean>(() => false);
  // Hold-M's start and end (`useMeasureTool`).
  const holdMeasureRef = useRef<{ start: () => void; end: () => void }>({ start: () => {}, end: () => {} });
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // Layer 0: a focused field keeps its own Esc, and the press never reaches the board.
        if (isTextEntry(e.target)) return;
        // The highest registered layer dismisses one entry (`escLayers`); with none registered,
        // clear the selection floor (layer 7; a no-op if nothing is selected).
        if (escRef.current.dismissTop()) return;
        // The docked command menu shows the selection, so clearing it here also hides the menu.
        if (selectedIdRef.current !== null) setSelectedId(null);
        return;
      }
      // Undo/redo on the encounter's single cross-mode stack; guarded so it never
      // fires while typing. Shift+mod+Z = redo, mod+Z = undo (mod = Cmd on macOS, Ctrl elsewhere).
      const mod = e.metaKey || e.ctrlKey;
      if (mod && (e.key === "z" || e.key === "Z")) {
        if (isTextEntry(e.target)) return;
        e.preventDefault();
        if (e.shiftKey) redoRef.current();
        else undoRef.current();
        return;
      }
      // The EDIT-mode authoring number keys (`decisions/0039`'s scheme, kept by `decisions/0191`): 1 is Select and 2–5 and 9 arm the
      // custom-cell brushes in the Tile palette's ADVANCED fold. 6–8, T, X and U are the rail's BUILD
      // entries, reached through `railKeyRef` above. EDIT-only + GM-gated (mirror `toolsDisabled`, the
      // same wall the rail buttons use), never while typing, and never with a modifier held (so it
      // can't shadow a browser/OS chord). Esc → Select is handled by the `authoring-tool` Esc entry
      // (layer 3).
      if (isTextEntry(e.target) || e.metaKey || e.ctrlKey || e.altKey) return;
      // Hold-M: a gesture in every mode for every viewer, disarming nothing. A repeat is the same hold.
      if (e.key.toLowerCase() === "m") {
        if (!e.repeat) holdMeasureRef.current.start();
        return;
      }
      // The tool rail's keys, in both modes and for every viewer, guarded like 1–9.
      if (railKeyRef.current(e.key)) return;
      const { viewMode: vm, toolsDisabled: td } = editKbRef.current;
      if (vm !== "edit" || td) return;
      switch (e.key) {
        case "1":
          setTool("select");
          setBrush(null);
          break;
        case "2":
          pickBrush("solid");
          break;
        case "3":
          pickBrush("cover");
          break;
        case "4":
          pickBrush("difficult");
          break;
        case "5":
          pickBrush("dangerous");
          break;
        case "9":
          pickBrush("pit");
          break;
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === "m") holdMeasureRef.current.end();
    };
    const onBlur = () => holdMeasureRef.current.end();
    window.addEventListener("keydown", onKey);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("blur", onBlur);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", onBlur);
    };
    // Subscribed once; the refs above carry the latest closures. pickBrush is stable
    // (useCallback []) and the useState setters are identity-stable, so capturing them here is safe.
  }, [pickBrush]);

  return { undoRef, redoRef, editKbRef, railKeyRef, holdMeasureRef, undoRedoSeq, bumpUndoRedoSeq };
}
